import cx from "classnames";
import { motion, useReducedMotion, Variants } from "framer-motion";
import { useState } from "react";
import { CaretRight } from "components/icons/CaretRight";
import { EntityStatusSelect } from "components/Entity/EntityStatusSelect";
import { EntityFlagAvatar } from "v2/components/onboarding/EntityFlagAvatar";
import { OverviewEntity } from "./brandOverview.types";
import { CARD_STAGGER_SEC, STATUS_TO_LEGACY } from "./constants";

type OverviewEntityCardProps = {
  entity: OverviewEntity;
  index: number;
  isSelected: boolean;
  onSelect: (entityId: string) => void;
};

// cardNudge — one-shot pop-in: scale 0.97 → 1.035 → 1. Box-shadow is handled
// via CSS (hover:shadow-l) so framer never leaves a shadow stranded inline.
export const cardNudge: Variants = {
  hidden: { opacity: 0, scale: 0.97 },
  visible: (index: number) => ({
    opacity: [0, 1, 1],
    scale: [0.97, 1.035, 1],
    transition: {
      duration: 0.6,
      delay: index * CARD_STAGGER_SEC,
      ease: [0.34, 1.2, 0.64, 1],
      times: [0, 0.45, 1],
    },
  }),
};

export const OverviewEntityCard = ({
  entity,
  index,
  isSelected,
  onSelect,
}: OverviewEntityCardProps) => {
  const reduceMotion = useReducedMotion();

  // after the entrance keyframe finishes, rest on a static target so hover-out
  // settles cleanly instead of replaying the pop keyframe
  const [hasEntered, setHasEntered] = useState(false);

  const statusEntity = {
    uuid: entity.id,
    entity_status: STATUS_TO_LEGACY[entity.status],
  };

  const handleSelect = () => onSelect(entity.id);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleSelect();
    }
  };

  return (
    <motion.div
      role="button"
      tabIndex={0}
      aria-pressed={isSelected}
      onClick={handleSelect}
      onKeyDown={handleKeyDown}
      custom={index}
      variants={reduceMotion ? undefined : cardNudge}
      initial={reduceMotion ? false : "hidden"}
      animate={
        reduceMotion || hasEntered ? { opacity: 1, scale: 1 } : "visible"
      }
      onAnimationComplete={() => setHasEntered(true)}
      whileHover={
        reduceMotion || isSelected
          ? undefined
          : { scale: 1.02, transition: { duration: 0.15, delay: 0 } }
      }
      whileTap={
        reduceMotion || isSelected
          ? undefined
          : { scale: 0.99, transition: { duration: 0.1, delay: 0 } }
      }
      className={cx(
        "t-group t-flex t-w-full t-cursor-pointer t-items-center t-justify-between t-gap-3 t-rounded-xl t-border t-border-solid t-p-4 t-transition-shadow",
        isSelected
          ? "t-border-purple-20 t-bg-surface-purple"
          : "t-border-neutral-10 t-bg-neutral-66 hover:t-shadow-l"
      )}
    >
      <div className="t-flex t-min-w-0 t-items-start t-gap-4">
        <div className="t-flex t-items-center t-py-1">
          <EntityFlagAvatar
            countryCode={entity.countryCode}
            size="lg"
            shape="circle"
          />
        </div>
        <div className="t-flex t-min-w-0 t-flex-col t-gap-1.5">
          <div className="t-flex t-min-w-0 t-flex-col t-gap-0.5">
            <span className="t-truncate t-text-subtext t-text-text-100">
              {entity.name}
            </span>
            <span className="t-text-body-sm t-text-neutral-40">
              {entity.entityTypeLabel}
            </span>
          </div>
          <div className="t-flex t-items-center t-gap-1.5 t-text-body-sm t-text-neutral-40">
            {Boolean(entity.countryName) && <span>{entity.countryName}</span>}
            {Boolean(entity.countryName) && Boolean(entity.homeStateName) && (
              <span className="t-h-0.5 t-w-0.5 t-rounded-full t-bg-neutral-40" />
            )}
            {Boolean(entity.homeStateName) && (
              <span>{entity.homeStateName}</span>
            )}
          </div>
        </div>
      </div>

      <div className="t-flex t-shrink-0 t-flex-col t-items-end t-justify-between t-gap-2 t-self-stretch">
        <EntityStatusSelect entity={statusEntity} />
        {!isSelected && (
          <>
            <span className="t-flex t-text-neutral-30 group-hover:t-hidden">
              <CaretRight className="t-h-3 t-w-3" />
            </span>
            <span className="t-hidden t-items-center t-gap-1 t-text-text-30 group-hover:t-flex">
              <span className="t-text-body-sm">View details</span>
              <CaretRight className="t-h-3 t-w-3" />
            </span>
          </>
        )}
      </div>
    </motion.div>
  );
};
